/* eslint-disable */
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import useMetadataUpdate from '../../../../hooks/useMetadataUpdate';
import { useQuery } from 'react-fetching-library';
import getFullName from '../../../../helpers/user';
import { Grid, makeStyles, Typography, Box } from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import Loader from '../../../Shared/Loader';
import { getLocalizedDate } from '../../../../helpers/localize';
import { PATIENT_LANGUAGE_BY_KEY } from '../../../Patients/constants';
import { formatPhoneForInfoPanel } from '../../../../helpers/phone';
import {
  openPatientPane,
  setCurrentPageData,
  toggleIsInfoPaneExpanded,
  navigateToRoute,
} from '../../../../redux_store';
import ListIcon from '@material-ui/icons/List';
import PatientEditModal from '../../../Patients/Item/Edit/PatientEditModal';
import { PATIENT_TABLE_NAMES, ALL_ROUTES } from '../../../../config/routes';
import InfoRow from '../../widgets/InfoRow';
import BaseInfoWidget from '../../widgets/BaseInfoWidget';
import { useParams } from 'react-router-dom';
import LoadingErrorWidget from '../../../Shared/LoadingErrorWidget';

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
  },
  form: {},
  anonymousPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 3),
    margin: theme.spacing(3, 0),
    minHeight: 200,
  },
  anonymousIcon: {
    fontSize: 48,
    color: theme.colors?.gray4 || '#818FA0',
    opacity: 0.5,
    marginBottom: theme.spacing(2),
  },
  anonymousTitle: {
    color: theme.colors?.gray4 || '#818FA0',
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },
  anonymousSubtitle: {
    color: theme.colors?.grey || '#8E8E8E',
    fontSize: '0.875rem',
  },
}));

let timeoutID;

const PatientInfo = () => {
  const {
    currentPage: { currentPageContext, currentPageData: patient },
  } = useSelector(state => state.infoPane);
  const {
    metadata: { currentAccount },
    isDistributionSponsor,
  } = useSelector(state => state.user);
  const {
    accountUuid: paramAccountUuid,
    patientUuid: paramsPatientUuid,
    currentAccountUuid,
  } = useParams();
  const [openPatientEditModal, setOPenPatientEditModal] = useState(false);
  const [canShowLoader, setCanShowLoader] = useState(false);
  const {
    en: { titles, forms, infoPane },
  } = useSelector(state => state.i18n);
  const dispatch = useDispatch();
  const classes = useStyles();

  useEffect(() => {
    if (
      (paramAccountUuid || currentAccountUuid) &&
      paramsPatientUuid &&
      paramsPatientUuid !== patient?.uuid
    ) {
      const newData = {
        accountUUID: paramAccountUuid || currentAccountUuid,
        patientUUID: paramsPatientUuid,
        patientFirstName: '',
        patientLastName: '',
        notExpand: true,
      };
      dispatch(openPatientPane(newData));
    }
  }, [paramsPatientUuid]);

  const {
    query,
    loading: isLoading,
    error,
  } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${currentPageContext.accountUUID}/patients/${
        currentPageContext.patientUUID ?? currentPageContext.uuid
      }`,
    },
    false,
  ]);

  const getPatientData = () => {
    query().then(({ error, payload }) => {
      if (!error) {
        dispatch(setCurrentPageData(payload));
      }
    });
  };

  useEffect(() => {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => {
      setCanShowLoader(true);
    }, 1500);
  }, []);

  useEffect(() => {
    if (query && !patient) {
      setCanShowLoader(false);
      clearTimeout(timeoutID);
      getPatientData();
      timeoutID = setTimeout(() => {
        setCanShowLoader(true);
      }, 1500);
    }
  }, [currentPageContext]);

  const openPatientPage = () => {
    const isItChildAccount =
      currentPageContext.accountUUID &&
      currentPageContext.accountUUID !== currentAccount.uuid &&
      isDistributionSponsor;
    dispatch(
      navigateToRoute({
        route: {
          path: (isItChildAccount ? ALL_ROUTES.patient.dashboardDS : ALL_ROUTES.patient.dashboard)
            .path,
          params: {
            currentAccountUuid: currentAccount.uuid,
            accountUuid: isItChildAccount ? currentPageContext.accountUUID : null,
            patientUuid: currentPageContext.patientUUID ?? currentPageContext.uuid,
            tableName: PATIENT_TABLE_NAMES.assessment,
          },
          title: `${titles.patientDashboard}: ${patient.patientFirstName ?? patient.firstName} ${
            patient.patientLastName ?? patient.lastName
          }`,
        },
      })
    );
    setTimeout(() => {
      dispatch(toggleIsInfoPaneExpanded(false));
    }, 1000);
  };

  const closePatientEditModal = wasUpdatedSuccessfully => {
    setOPenPatientEditModal(false);
    if (wasUpdatedSuccessfully) {
      getPatientData();
    }
  };

  const values = useMemo(() => {
    if (!patient) {
      return [];
    }
    if (patient?.isAnonymousPatient) {
      return [
        ...(isDistributionSponsor
          ? [
              {
                title: forms.bussinessName,
                info: patient.accountBusinessName || '-',
              },
            ]
          : []),
      ];
    }

    const { streetAddress, suite, city, state, zipCode } = patient.address;

    const address = `${streetAddress ? streetAddress : ''}${streetAddress && suite ? ' ' : ''}${
      suite ? suite : ''
    }`;
    const city_state = `${city ? city : ''}${city && state ? ', ' : ''}${state ? state : ''}`;
    const zip = `${zipCode ? zipCode : ''}`;

    const addressValue = [
      ...(address ? [address] : []),
      ...(city_state ? [city_state] : []),
      ...(zip ? [zip] : []),
    ];

    const addressForCopying =
      address +
      (address && city_state ? ', ' : '') +
      city_state +
      ((address || city_state) && zip ? ' ' : '') +
      zip;

    return [
      ...(isDistributionSponsor
        ? [
            {
              title: forms.bussinessName,
              info: patient.accountBusinessName || '-',
            },
          ]
        : []),
      {
        title: forms.mrnNumber,
        info: patient.mrnNumber,
      },
      {
        title: forms.firstName,
        info: patient.firstName,
      },
      {
        title: forms.lastName,
        info: patient.lastName,
      },
      {
        title: forms.phone,
        info: patient?.phone?.phone ? formatPhoneForInfoPanel(patient?.phone?.phone) : '-',
      },
      {
        title: forms.email,
        info: patient.email,
        dataActionTooltipTitle: infoPane.sentEmail,
        dataAction: (e) => {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = `mailto:${patient.email}`;
        },
        dataActionSecondary: (e) => {
          e.preventDefault();
          e.stopPropagation();
          const recipient = patient.email;
          const subject = encodeURIComponent('');
          const body = encodeURIComponent('');
          window.open(
            `https://outlook.office.com/mail/deeplink/compose?to=${recipient}&subject=${subject}&body=${body}`,
            '_blank',
          );
        },
      },
      {
        title: forms.dateOfBirth,
        info: getLocalizedDate(patient.dateOfBirth, 'UTC'),
      },
      {
        title: forms.genderAtBirth,
        info: forms[patient.genderAtBirth],
      },
      {
        title: forms.drugAllergies,
        info: patient.hasDrugAllergies ? forms.yes : forms.no,
      },
      {
        title: forms.address,
        info: addressValue.length ? addressValue : '',
        customCopyValue: addressForCopying,
      },
      {
        title: forms.patientLanguage,
        info: PATIENT_LANGUAGE_BY_KEY[patient.patientLanguage],
      },
    ];
  }, [patient]);

  return (
    <BaseInfoWidget
      breadcrumbs={infoPane.patient}
      title={getFullName(patient ?? currentPageContext)}
      editButton={
        patient?.isAnonymousPatient
          ? null
          : {
              hint: infoPane.editPatient,
              onClick: () => setOPenPatientEditModal(true),
              disabled: isLoading || !patient,
            }
      }
      actions={
        patient?.isAnonymousPatient
          ? []
          : [
              {
                icon: <ListIcon />,
                tooltipTitle: infoPane.openPatientDashboard,
                buttonTitle: infoPane.openPatientDashboard,
                disabled: isLoading || !patient,
                onClick: openPatientPage,
              },
            ]
      }
      content={
        <div className={classes.container}>
          {!error && (isLoading || !patient) ? (
            canShowLoader ? (
              <Loader fullHeight />
            ) : (
              <div />
            )
          ) : (
            <div className={classes.form}>
              <Grid container direction={'column'}>
                {values.map(props => (
                  <InfoRow {...props} />
                ))}
              </Grid>
              {patient?.isAnonymousPatient && values.length <= 1 && (
                <Box className={classes.anonymousPlaceholder}>
                  <LockOutlinedIcon className={classes.anonymousIcon} />
                  <Typography variant="body1" className={classes.anonymousTitle}>
                    {infoPane.anonymousPatient}
                  </Typography>
                  <Typography variant="body2" className={classes.anonymousSubtitle}>
                    {infoPane.anonymousPatientInfo}
                  </Typography>
                </Box>
              )}
            </div>
          )}
          {error && (
            <div style={{ padding: 24 }}>
              <LoadingErrorWidget onReload={getPatientData} />
            </div>
          )}
          <PatientEditModal
            open={openPatientEditModal}
            patientUuid={currentPageContext.patientUUID ?? currentPageContext.uuid}
            accountUuid={currentPageContext.accountUUID}
            handleClose={closePatientEditModal}
          />
        </div>
      }
    />
  );
};

export default PatientInfo;
